import React, { useState, useMemo, Fragment } from 'react'
import { Link } from 'react-router-dom'
import { useSet } from 'react-use'
import styled from 'styled-components'
import classNames from 'classnames'
import AspectRatio from 'components/AspectRatio'
import Marker from './Marker'
import { GovernmentIcon, RunLineIcon, SchoolIcon, StoreIcon } from 'components/Icons'

export const View = styled.div`
  display: ${(props) => (props.show ? 'block' : 'none')};

  svg {
    font-family: 'GT Walsheim Pro Regular';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }
`

export const Categories = styled.div`
  display: ${(props) => (props.show ? 'block' : 'none')};

  @media (min-width: 768px) {
    width: 200px;
  }
`

export const PolygonButton = styled.button`
  @media (min-width: 768px) {
    clip-path: polygon(97% 0, 100% 50%, 97% 100%, 0 100%, 0 0);
  }
`

export const ActionPanel = styled.div`
  &,
  button {
    font-family: 'GT Walsheim Pro Medium';
  }

  @media (min-width: 768px) {
    position: absolute;
    top: 10px;
    left: 0;
    width: 240px;
    z-index: 1001;
  }

  @media (min-width: 992px) {
    top: 50px;
    width: 300px;
  }
`

const Lod = ({ title, href, pathProps, textProps }) => (
  <Link to={href} className="text-white">
    <path className="text-primary" fill="currentColor" {...pathProps} />
    <text fill="currentColor" {...textProps}>
      {title}
    </text>
  </Link>
)

const Map = ({ categories, markers, images, views }) => {
  const categoryAccessors = React.useMemo(() => categories.map((c) => c.accessor), [])
  const categorySet = new Set(categoryAccessors)
  const [lodir, thjonustukort] = views
  const [activeCategories, { toggle: toggleActiveCategory }] = useSet(categorySet)
  const [activeView, setActiveView] = useState('lodir')

  const categoriesWithMarkers = useMemo(() => {
    const enabledCategories = categories.filter((c) => c.enabled)

    return enabledCategories.map((category) => {
      category.markers = markers.filter((marker) => marker.tegund === category.accessor)

      return category
    })
  }, [categories, markers])

  const ratios = {
    thjonustukort: 1226.8 / 2145.5,
    lodir: 1375 / 1907,
  }

  return (
    <div className="position-relative">
      <ActionPanel>
        {views.map(({ accessor, title }) => (
          <PolygonButton
            key={accessor}
            onClick={() => setActiveView(accessor)}
            className={classNames(
              'btn text-uppercase d-block w-100 border-0',
              activeView === accessor ? 'btn-primary' : 'btn-white'
            )}
          >
            {title}
          </PolygonButton>
        ))}

        <Categories className="bg-white py-3 mt-md-2 mt-lg-4" show={activeView === 'thjonustukort'}>
          {categoriesWithMarkers.map(({ accessor, title, color, Icon }) => (
            <button
              key={accessor}
              className="d-flex align-items-center w-100 border-0 bg-transparent text-left px-4 py-2"
              style={activeCategories.has(accessor) ? { color } : {}}
              onClick={() => toggleActiveCategory(accessor)}
            >
              <Icon width="44px" viewBox="0 0 36 36" />
              <span className="d-block ml-2">{title}</span>
            </button>
          ))}
        </Categories>
      </ActionPanel>

      <AspectRatio ratio={ratios[activeView]}>
        <div className="position-absolute" style={{ top: 0, left: 0, right: 0 }}>
          <View show={activeView === 'thjonustukort'}>
            <img className="w-100" src={images.thjonustukort} alt="Þjónustukort og lóðir" />
            <svg viewBox="0 0 2145.5 1226.80">
              {categoriesWithMarkers.map(({ accessor, color, Icon, iconId, markers }) => (
                <Fragment key={accessor}>
                  <defs>
                    <Icon className="cursor-pointer" style={{ color }} id={iconId} />
                  </defs>

                  <g className={classNames({ 'd-none': !activeCategories.has(accessor) })}>
                    {markers.map(({ id, ...marker }) => (
                      <Marker {...marker} key={id} bg={color} iconHref={`#${iconId}`} />
                    ))}
                  </g>
                </Fragment>
              ))}

              {thjonustukort.items.map((item) => (
                <Lod key={item.href} {...item} />
              ))}
            </svg>
          </View>

          <View show={activeView === 'lodir'}>
            <img className="w-100" src={images.lodir} alt="Lóðir" />

            <svg viewBox="0 0 1907 1375">
              {lodir.items.map((item) => (
                <Lod key={item.href} {...item} />
              ))}
            </svg>
          </View>
        </div>
      </AspectRatio>
    </div>
  )
}

Map.defaultProps = {
  images: {
    lodir: require('images/thjonustukort-skoda-lodir.png'),
    thjonustukort: require('images/thjonustukort-n.png'),
  },
  views: [
    {
      accessor: 'lodir',
      title: 'Skoða lóðir',
      items: [
        {
          title: 'Flóahverfið iðnaðarlóðir',
          href: 'lodir/floahverfid',
          pathProps: {
            className: 'text-info',
            d: 'M1240.2 376.09v-32.8s.27-16.26 8.8-25.86 172-218.14 172-218.14l53.2-60.73 53.87 44.8-238.4 292.73z',
          },
          textProps: {
            transform: 'rotate(-51 977 -1219)',
            fontSize: '19pt',
          },
        },
        {
          title: 'Sementsreitur',
          href: 'lodir/sementsreitur',
          pathProps: {
            d: 'M635.73 818.29s24.54-18.4 37.07-23.2a149 149 0 0135.2-9.33c14.13-2.13 21.6-2.13 21.6-2.13h13.6s19.2-1.87 22.67-9.34 15.2-30.4 15.2-30.4v-5.33l-66.67-10.4s-9.6-4-10.4-8.8a93.49 93.49 0 00-2.66-10.67l-12.8 8.8-37.06 30.4-30.14 21.6s2.14 12.8 0 16.27 1.87 17.33 1.87 17.33z',
          },
          textProps: {
            transform: 'rotate(-15 3303 -2048)',
            fontSize: '14pt',
          },
        },
        {
          title: 'Dalbraut',
          href: 'lodir/dalbraut',
          pathProps: {
            d: 'M758.27 671.03l21.86 15.77 3.74-1.33 3.73-1.87 3.47-2.67 22.4-23.2-25.87-19.2z',
          },
          textProps: {
            transform: 'translate(768 665)',
            fontSize: '7pt',
          },
        },
        {
          title: 'Skógarhverfi',
          href: 'lodir/skogarhverfi',
          pathProps: {
            className: 'text-info',
            d: 'M1139.73 528.36l-25.6 42.93-16.26 23.51-9.34 9.07 59.74 53.33s8.8 5.87 32.8 5.33 24-7.73 24-7.73l43.46-72.27z',
          },
          textProps: {
            transform: 'translate(1117 600)',
            fontSize: '14pt',
          },
        },
      ],
    },
    {
      accessor: 'thjonustukort',
      title: 'Sjá þjónustukort',
      items: [
        {
          title: 'Sementsreitur',
          href: 'lodir/sementsreitur',
          pathProps: {
            d: 'M909,720.15s38.5-28.87,58.16-36.4,33.05-11.3,55.23-14.64,33.89-3.35,33.89-3.35h21.34s30.13-2.93,35.57-14.65,23.85-47.7,23.85-47.7v-8.36l-104.61-16.32s-15.06-6.28-16.31-13.81a145.77,145.77,0,0,0-4.19-16.74L991.85,562l-58.16,47.7-47.27,33.86s3.35,20.09,0,25.53,2.93,27.2,2.93,27.2Z',
          },
          textProps: {
            transform: 'translate(930 660) rotate(-15)',
            fontSize: '19pt',
          },
        },
        {
          title: 'Skógarhverfi',
          href: 'lodir/skogarhverfi',
          pathProps: {
            className: 'text-info',
            d: 'M1699.84,265.22l-40.17,67.33-25.52,36.82-14.65,14.22,93.73,83.69s13.81,9.21,51.47,8.37,37.65-12.1,37.65-12.1l68.2-113.39Z',
          },
          textProps: {
            transform: 'translate(1678 400) rotate(-15)',
            fontSize: '19pt',
          },
        },
        {
          title: 'Dalbraut',
          href: 'lodir/dalbraut',
          pathProps: {
            d: 'M1101.29,487.08l34.31,24.68,5.85-2.09,5.86-2.93,5.44-4.18,35.15-36.4L1147.31,436Z',
          },
          textProps: {
            transform: 'translate(1115 490) rotate(-15)',
            fontSize: '12pt',
          },
        },
      ],
    },
  ],
  categoryAccessors: ['skolar', 'stjornsysla', 'ithrottir', 'verslun'],
  categories: [
    {
      enabled: true,
      accessor: 'skolar',
      title: 'Skólar',
      Icon: SchoolIcon,
      iconId: 'skolar-icon',
      color: '#101092',
      markers: [],
    },
    {
      enabled: true,
      accessor: 'stjornsysla',
      title: 'Stjórnsýsla',
      Icon: GovernmentIcon,
      iconId: 'stjornsysla-icon',
      color: '#2997E8',
      markers: [],
    },
    {
      enabled: true,
      accessor: 'ithrottir',
      title: (
        <>
          Íþróttir og
          <br />
          útivist
        </>
      ),
      Icon: RunLineIcon,
      iconId: 'ithrottir-icon',
      color: '#12AF4C',
      markers: [],
    },
    {
      enabled: false,
      accessor: 'verslun',
      title: (
        <>
          Verslun og
          <br />
          þjónusta
        </>
      ),
      Icon: StoreIcon,
      iconId: 'verslun-icon',
      color: '#9C2F00',
      markers: [],
    },
  ],
}

export default React.memo(Map)
